import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Form, Typography, Input, Button, Spin, message } from "antd";
import "./FormContact.scss";
import contactUsSchema from "../../../../validations/contactUsValidation";
import { createYupValidator } from "../../../../utils/validationHelper";
import { submitContactForm } from "../../../../features/contactUs/contactUsActions";
import { staticTextErrors } from "../../../../config/constantErrorTexts";

const { Text } = Typography;
const { TextArea } = Input;

const FORM_FIELDS = ["first_name", "last_name", "email", "subject", "message"];

export default function FormContact() {
  const formRef = useRef(null);
  const [messageApi, contextHolder] = message.useMessage();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [backendError, setBackendError] = useState(null);

  const clearFieldError = (fieldName) => {
    if (backendError && backendError[fieldName]) {
      const updated = { ...backendError };
      delete updated[fieldName];
      setBackendError(Object.keys(updated).length ? updated : null);
      formRef?.current?.setFields([
        {
          name: fieldName,
          errors: [],
        },
      ]);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setBackendError(null);
    try {
      const response = await dispatch(submitContactForm(values)).unwrap();
      formRef?.current?.resetFields();
      messageApi.open({
        type: "success",
        content: response?.message || "Message Sent Successfully!",
      });
      setLoading(false);
      setBackendError(null);
    } catch (error) {
      if (typeof error === "object") {
        setBackendError(error);
        let hasFieldError = false;

        for (let key in error) {
          if (FORM_FIELDS.includes(key)) {
            formRef?.current?.setFields([
              {
                name: key,
                errors: [error[key][0]],
              },
            ]);
            hasFieldError = true;
          }
        }

        if (!hasFieldError) {
          if (error?.detail) {
            messageApi.open({
              type: "error",
              content: error.detail,
            });
          } else if (error?.SERVER_ERROR) {
            messageApi.open({
              type: "error",
              content: staticTextErrors.SERVER_ERROR,
            });
          } else {
            messageApi.open({
              type: "error",
              content: staticTextErrors.SERVER_ERROR,
            });
          }
        }
      } else {
        messageApi.open({
          type: "error",
          content: staticTextErrors.SERVER_ERROR,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Spin spinning={loading} size="large">
        <Form
          className="contactForm"
          ref={formRef}
          layout="vertical"
          onFinish={(values) => {
            handleSubmit(values);
          }}>
          <div className="contactForm__formItemWrapper">
            <Form.Item
              className="contactForm__formItemWrapper__formItem"
              name="first_name"
              rules={[
                {
                  required: true,
                  validator: createYupValidator(contactUsSchema, "first_name"),
                },
              ]}
              label={<Text>First Name</Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="First Name"
                size="large"
                onChange={() => clearFieldError("first_name")}
              />
            </Form.Item>
            <Form.Item
              className="contactForm__formItemWrapper__formItem"
              name="last_name"
              rules={[
                {
                  required: true,
                  validator: createYupValidator(contactUsSchema, "last_name"),
                },
              ]}
              label={<Text>Last Name</Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="Last Name"
                size="large"
                onChange={() => clearFieldError("last_name")}
              />
            </Form.Item>
          </div>
          <div className="contactForm__formItemWrapper">
            <Form.Item
              className="contactForm__formItemWrapper__formItem"
              name="email"
              rules={[
                {
                  required: true,
                  validator: createYupValidator(contactUsSchema, "email"),
                },
              ]}
              label={<Text>Email</Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="Email"
                size="large"
                onChange={() => clearFieldError("email")}
              />
            </Form.Item>
            <Form.Item
              className="contactForm__formItemWrapper__formItem"
              name="subject"
              rules={[
                {
                  required: true,
                  validator: createYupValidator(contactUsSchema, "subject"),
                },
              ]}
              label={<Text>Subject</Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="Subject"
                size="large"
                onChange={() => clearFieldError("subject")}
              />
            </Form.Item>
          </div>
          <div className="contactForm__formItemWrapper">
            <Form.Item
              className="contactForm__formItemWrapper__formItem"
              name="message"
              rules={[
                {
                  required: true,
                  validator: createYupValidator(contactUsSchema, "message"),
                },
              ]}
              label={<Text>Message</Text>}
              colon={false}>
              <TextArea
                placeholder="Message"
                size="large"
                className="contactForm__formItemWrapper__formItem__textArea"
                rows={5}
                onChange={() => clearFieldError("message")}
              />
            </Form.Item>
          </div>

          <Button
            size="large"
            type="primary"
            block
            htmlType="submit"
            disabled={loading}>
            Submit
          </Button>
        </Form>
      </Spin>
    </>
  );
}
